import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { getSensorColor, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { ColorSwatch } from 'features/timeSeries/components/ColorSwatch'
import { EditableRangeInput } from 'features/timeSeries/components/EditableRangeInput'
import { YScaleControls } from 'features/timeSeries/components/YScaleControls'
import { getSharedAutoRange } from 'features/timeSeries/utils/axisRangeHelpers'
import { getAllActiveItems } from 'features/timeSeries/utils/timeSeriesUtils'
import { RotateCcw, Trash2 } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { MOBILE_LAYOUT_QUERY } from 'utils/mobileLayout'

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ControlsWrap = styled.div`
  padding: 0.25rem 0.5rem 0.625rem;
  border-bottom: 1px solid var(--border);
`

const ROW_GRID = `
  display: grid;
  grid-template-columns: 0.875rem minmax(0, 1fr) 4rem 4rem 1.375rem 1.375rem;
  align-items: center;
  column-gap: 0.375rem;
  padding: 0 0.5rem;
`

const HeaderRow = styled.div`
  ${ROW_GRID}
  height: 1.375rem;
  font-size: 0.5625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted-foreground);
  border-bottom: 1px solid var(--border);
`

const HeaderCell = styled.span``

const PenRow = styled.div`
  ${ROW_GRID}
  height: 1.875rem;

  @media ${MOBILE_LAYOUT_QUERY} {
    min-height: 2.75rem;
    height: auto;
    padding-block: 0.375rem;
  }

  &:hover {
    background: var(--muted);
  }
`

const PenName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.6875rem;
`

const RangeChip = styled.span`
  display: inline-flex;

  input {
    width: 100%;
    height: 1.125rem;
    padding: 0 0.25rem;
    font-size: 0.625rem;
    text-align: right;

    @media ${MOBILE_LAYOUT_QUERY} {
      height: 2rem;
      font-size: 1rem;
    }
  }
`

const EmptyHint = styled.div`
  padding: 0.5rem;
  font-size: 0.6875rem;
  color: var(--muted-foreground);
`

interface TagConfigurationListProps {
  showControls: boolean
}

export const TagConfigurationList: React.FC<TagConfigurationListProps> = ({
  showControls,
}) => {
  const { t } = useLingui()

  const {
    customSensorColors,
    selectedSensors,
    setSelectedSensors,
    handleYMinChange,
    handleYMaxChange,
    handleClearAxisRange,
    sensorRanges,
    customAxisRanges,
    handleChangeColor,
    singleScaleMode,
    derivedValues,
    isLoading,
    isInitializing,
  } = useScatterplotContext()

  const handleRemove = useCallback(
    (sensorId: string) => {
      setSelectedSensors((prev) =>
        prev.filter((sensor) => sensor.id !== sensorId)
      )
    },
    [setSelectedSensors]
  )

  // Single-scale placeholder: the axis renders ONE shared auto range, so
  // empty cells advertise that shared range rather than each pen's own.
  const sharedAutoRange = useMemo(
    () =>
      singleScaleMode
        ? getSharedAutoRange(
            getAllActiveItems(selectedSensors, derivedValues),
            sensorRanges
          )
        : null,
    [singleScaleMode, selectedSensors, derivedValues, sensorRanges]
  )
  const rangesSettled = !isLoading && !isInitializing

  // Derived pens were counted for the shared scale but had no row, so a KPI or a
  // residual could not be given a y-range of its own — and a residual around
  // zero shares no sensible axis with a temperature in °C.
  const derivedRows = useMemo(
    () =>
      derivedValues
        .filter((derived) =>
          derived.data.dependencyIds.every(
            (depId) =>
              depId === 'timestamp' ||
              selectedSensors.some((sensor) => sensor.id === depId) ||
              derivedValues.some((other) => other.id === depId)
          )
        )
        .map((derived) => ({ id: derived.id, name: derived.data.name })),
    [derivedValues, selectedSensors]
  )

  const placeholderFor = (sensorId: string, edge: 'min' | 'max') => {
    const value = singleScaleMode
      ? sharedAutoRange?.[edge]
      : sensorRanges[sensorId]?.[edge]
    if (value !== undefined && value !== null) return String(value)
    return rangesSettled ? t`No data` : ''
  }

  return (
    <ListContainer>
      {showControls && (
        <ControlsWrap>
          <YScaleControls hideAuto />
        </ControlsWrap>
      )}
      {selectedSensors.length === 0 && derivedRows.length === 0 && (
        <EmptyHint>
          <Trans>Nothing plotted yet.</Trans>
        </EmptyHint>
      )}
      {(selectedSensors.length > 0 || derivedRows.length > 0) && (
        <HeaderRow>
          <span />
          <HeaderCell>
            <Trans>Tag</Trans>
          </HeaderCell>
          <HeaderCell>
            <Trans>Min</Trans>
          </HeaderCell>
          <HeaderCell>
            <Trans>Max</Trans>
          </HeaderCell>
          <span />
          <span />
        </HeaderRow>
      )}
      {selectedSensors.map((sensor, index) => {
        const color = customSensorColors[sensor.id] || getSensorColor(index)
        const tooltip = sensor.data.description
          ? `${sensor.data.name} - ${sensor.data.description}`
          : sensor.data.name
        return (
          <PenRow key={sensor.id}>
            <ColorSwatch
              size='sm'
              color={color}
              onChangeColor={(next) => handleChangeColor(sensor.id, next)}
            />
            <ChartTooltip label={tooltip} side='top'>
              <PenName>{sensor.data.name}</PenName>
            </ChartTooltip>
            <ChartTooltip label={t`Y min override`} side='top'>
              <RangeChip>
                <EditableRangeInput
                  hideClear
                  value={customAxisRanges[sensor.id]?.min}
                  placeholder={placeholderFor(sensor.id, 'min')}
                  onCommit={(value) => handleYMinChange(sensor.id, value)}
                  ariaLabel={t`${{ value204_34: sensor.data.name }} Y min`}
                />
              </RangeChip>
            </ChartTooltip>
            <ChartTooltip label={t`Y max override`} side='top'>
              <RangeChip>
                <EditableRangeInput
                  hideClear
                  value={customAxisRanges[sensor.id]?.max}
                  placeholder={placeholderFor(sensor.id, 'max')}
                  onCommit={(value) => handleYMaxChange(sensor.id, value)}
                  ariaLabel={t`${{ value215_34: sensor.data.name }} Y max`}
                />
              </RangeChip>
            </ChartTooltip>
            <ChartTooltip label={t`Reset Y range to auto`} side='top'>
              <Button
                variant='ghostMuted'
                size='icon-xs'
                aria-label={t`Reset ${{ value223_39: sensor.data.name }} Y range to auto`}
                disabled={
                  customAxisRanges[sensor.id]?.min === undefined &&
                  customAxisRanges[sensor.id]?.max === undefined
                }
                onClick={() => handleClearAxisRange(sensor.id)}>
                <RotateCcw size={11} />
              </Button>
            </ChartTooltip>
            <ChartTooltip label={t`Remove pen`} side='top'>
              <Button
                variant='ghostMuted'
                size='icon-xs'
                aria-label={t`Remove pen`}
                onClick={() => handleRemove(sensor.id)}>
                <Trash2 size={11} />
              </Button>
            </ChartTooltip>
          </PenRow>
        )
      })}
      {derivedRows.map((item, index) => {
        const color =
          customSensorColors[item.id] ||
          getSensorColor(selectedSensors.length + index)
        return (
          <PenRow key={item.id}>
            <ColorSwatch
              size='sm'
              color={color}
              onChangeColor={(next) => handleChangeColor(item.id, next)}
            />
            <ChartTooltip
              label={t`${{ value255_38: item.name }} — computed`}
              side='top'>
              <PenName>{item.name}</PenName>
            </ChartTooltip>
            <ChartTooltip label={t`Y min override`} side='top'>
              <RangeChip>
                <EditableRangeInput
                  hideClear
                  value={customAxisRanges[item.id]?.min}
                  placeholder={placeholderFor(item.id, 'min')}
                  onCommit={(value) => handleYMinChange(item.id, value)}
                  ariaLabel={t`${{ value265_34: item.name }} Y min`}
                />
              </RangeChip>
            </ChartTooltip>
            <ChartTooltip label={t`Y max override`} side='top'>
              <RangeChip>
                <EditableRangeInput
                  hideClear
                  value={customAxisRanges[item.id]?.max}
                  placeholder={placeholderFor(item.id, 'max')}
                  onCommit={(value) => handleYMaxChange(item.id, value)}
                  ariaLabel={t`${{ value276_34: item.name }} Y max`}
                />
              </RangeChip>
            </ChartTooltip>
            <ChartTooltip label={t`Reset Y range to auto`} side='top'>
              <Button
                variant='ghostMuted'
                size='icon-xs'
                aria-label={t`Reset ${{ value284_39: item.name }} Y range to auto`}
                disabled={
                  customAxisRanges[item.id]?.min === undefined &&
                  customAxisRanges[item.id]?.max === undefined
                }
                onClick={() => handleClearAxisRange(item.id)}>
                <RotateCcw size={11} />
              </Button>
            </ChartTooltip>
            <span />
          </PenRow>
        )
      })}
    </ListContainer>
  )
}
